import { CANCELLATION_NOTE_MAX, CANCELLATION_REASONS, CancellationReason } from '@app/constants/cancellationReasons';
import { useUser } from '@app/context/UserContext';
import { Spinner } from '@components/ui';
import { AlertDialog } from '@components/ui/AlertDialog/AlertDialog';
import { Textarea } from '@components/ui/Textarea/Textarea';
import { useCompanyBasicQuery } from '@dieterApi/company/useCompanyQuery';
import { usePromoCodeQuery } from '@dieterApi/subscription/usePromoCodeQuery';
import { useSubscriptionCancelMutation } from '@dieterApi/subscription/useSubscriptionCancelMutation';
import { useSubscriptionUndoCancelMutation } from '@dieterApi/subscription/useSubscriptionUndoCancelMutation';
import { useSubscriptionsQuery } from '@dieterApi/subscription/useSubscriptionsQuery';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { NotificationModal } from '..';

interface Props {
  open: boolean;
  onClose: () => void;
  promoCodeId?: string;
}

export function ManageSubscriptionModal({ open, onClose, promoCodeId }: Props) {
  const { t, i18n } = useTranslation();
  const { user } = useUser();
  // Subscriptions of Wix users are managed through Wix, not Stripe
  const { data: companyData } = useCompanyBasicQuery(user?.company?.id || '');
  const isWixManaged = !!companyData?.getCompany?.wixAppInstanceId;
  const { data, loading, error, refetch } = useSubscriptionsQuery({
    skip: !open || !user || isWixManaged,
    fetchPolicy: 'network-only',
  });
  const subs = data?.subscriptions || [];
  const [cancelSubId, setCancelSubId] = React.useState<string | null>(null);
  const [undoSubId, setUndoSubId] = React.useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  // optionaler Kuendigungsgrund (constants/cancellationReasons.ts), Default "keine Angabe"
  const [cancelReason, setCancelReason] = React.useState<CancellationReason>('no_answer');
  const [cancelNote, setCancelNote] = React.useState('');
  const [undoConfirmOpen, setUndoConfirmOpen] = React.useState(false);
  const [runCancel, { loading: cancelLoading, error: cancelError }] = useSubscriptionCancelMutation();
  const [runUndoCancel, { loading: undoLoading, error: undoError }] = useSubscriptionUndoCancelMutation();

  // Fetch promo code details if promoCodeId is provided
  const {
    data: promoData,
    loading: promoLoading,
    error: promoError,
  } = usePromoCodeQuery({
    variables: { promoCodeId: promoCodeId || '' },
    skip: !promoCodeId || !open || !user,
    fetchPolicy: 'network-only',
  });

  // Only use promo code if it's valid and active
  const promoCode = promoData?.promoCode?.valid && promoData?.promoCode?.active ? promoData.promoCode : undefined;

  const locale = React.useMemo(() => {
    const lang = (i18n.language || 'de').toLowerCase();
    if (lang.startsWith('de')) return 'de-DE';
    if (lang.startsWith('fr')) return 'fr-FR';
    return 'en-US';
  }, [i18n.language]);

  // Check if any subscription has a discount
  const hasAnyDiscount = React.useMemo(() => {
    return subs.some((s) => s.discount != null);
  }, [subs]);

  const badgeColor = (status: string): 'default' | 'success' | 'warning' | 'error' | 'info' => {
    switch (status) {
      case 'active':
        return 'success';
      case 'trialing':
        return 'info';
      case 'past_due':
        return 'warning';
      case 'canceled':
        return 'default';
      case 'unpaid':
        return 'error';
      default:
        return 'default';
    }
  };

  const statusLabel = (status: string): string => t(`components.ui.manage_subscription.status.${status}`, status);

  const displayStatus = (status: string, cancelAt?: string | null): string => (cancelAt ? 'canceled' : status);

  const onCancel = (id: string) => {
    setCancelSubId(id);
    setCancelReason('no_answer');
    setCancelNote('');
    setConfirmOpen(true);
  };

  const onUndoCancel = (id: string) => {
    setUndoSubId(id);
    setUndoConfirmOpen(true);
  };

  const selected = subs.find((s) => s.id === cancelSubId);
  const isTrial = selected?.status === 'trialing';
  const contractualEnd = selected ? new Date(selected.endDate).toLocaleDateString(locale) : '';

  const undoSelected = subs.find((s) => s.id === undoSubId);
  const undoCancelAt = undoSelected?.cancelAt ? new Date(undoSelected.cancelAt).toLocaleDateString(locale) : '';

  return (
    <>
      <NotificationModal open={open} onClose={onClose} maxWidth="1200px">
        <div className="p-4 md:p-9 flex flex-col gap-6 md:gap-10 relative">
          {/* Loading overlay for mutations */}
          {(cancelLoading || undoLoading) && (
            <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-50 rounded">
              <div className="flex flex-col items-center gap-3">
                <Spinner />
                <p className="text-gray-600">
                  {cancelLoading
                    ? t('components.ui.manage_subscription.loading.cancel', 'Kündigung wird verarbeitet...')
                    : t('components.ui.manage_subscription.loading.undo', 'Wird rückgängig gemacht...')}
                </p>
              </div>
            </div>
          )}
          <h2 className="text-center font-bold text-lg md:text-xl mb-4 md:mb-12">
            {t('components.ui.manage_subscription.title')}
          </h2>
          {isWixManaged && (
            <p className="text-center text-gray-700 max-w-xl mx-auto">
              {t(
                'components.ui.manage_subscription.wix_notice',
                'Dein Abonnement wird über Wix verwaltet. Änderungen oder eine Kündigung kannst du daher ganz einfach direkt in deinem Wix-Konto vornehmen.'
              )}
            </p>
          )}
          {error && !loading && (
            <p className="text-center text-red-600">
              {t('components.ui.manage_subscription.error.load', { message: error.message })}
            </p>
          )}
          {cancelError && (
            <p className="text-center text-red-600">
              {t('components.ui.manage_subscription.error.cancel', { message: cancelError.message })}
            </p>
          )}
          {undoError && (
            <p className="text-center text-red-600">
              {t('components.ui.manage_subscription.error.undo', { message: undoError.message })}
            </p>
          )}
          {promoError && !promoLoading && (
            <p className="text-center text-orange-600">
              {t('components.ui.manage_subscription.error.promo', { message: promoError.message })}
            </p>
          )}
          {promoCodeId && promoData?.promoCode && (!promoData.promoCode.valid || !promoData.promoCode.active) && (
            <p className="text-center text-orange-600">
              {t(
                'components.ui.manage_subscription.error.promo_invalid',
                'Der Aktionscode ist ungültig oder abgelaufen.'
              )}
            </p>
          )}
          {/* Desktop Table View - Hidden on mobile */}
          {!isWixManaged && (
            <div className="hidden lg:block overflow-x-auto">
              <table className="min-w-full text-left border-collapse">
                <thead>
                  <tr className="border-b">
                    <th className="py-2 px-3">{t('components.ui.manage_subscription.table.status')}</th>
                    <th className="py-2 px-3">{t('components.ui.manage_subscription.table.start')}</th>
                    <th className="py-2 px-3">{t('components.ui.manage_subscription.table.next_invoice')}</th>
                    <th className="py-2 px-3">{t('components.ui.manage_subscription.table.payment_interval')}</th>
                    {hasAnyDiscount && (
                      <th className="py-2 px-3">{t('components.ui.manage_subscription.table.discount')}</th>
                    )}
                    <th className="py-2 px-3">{t('components.ui.manage_subscription.table.contract_end')}</th>
                    <th className="py-2 px-3">{t('components.ui.manage_subscription.table.products')}</th>
                    <th className="py-2 px-3 text-right"></th>
                  </tr>
                </thead>
                <tbody>
                  {loading
                    ? Array.from({ length: 1 }).map((_, i) => (
                        <tr key={`sk-${i}`} className="border-b align-top">
                          <td className="py-3 px-3">
                            <Skeleton variant="rounded" width={64} height={24} />
                          </td>
                          <td className="py-3 px-3">
                            <Skeleton width={100} />
                          </td>
                          <td className="py-3 px-3">
                            <div className="flex flex-col gap-1">
                              <Skeleton width={120} />
                              <Skeleton width={90} />
                            </div>
                          </td>
                          <td className="py-3 px-3">
                            <Skeleton width={90} />
                          </td>
                          {hasAnyDiscount && (
                            <td className="py-3 px-3">
                              <Skeleton width={90} />
                            </td>
                          )}
                          <td className="py-3 px-3">
                            <Skeleton width={120} />
                          </td>
                          <td className="py-3 px-3">
                            <div className="flex flex-col gap-1">
                              <Skeleton width={220} />
                              <Skeleton width={180} />
                            </div>
                          </td>
                          <td className="py-3 px-3 text-right">
                            <Skeleton variant="rounded" width={110} height={32} className="inline-block" />
                          </td>
                        </tr>
                      ))
                    : subs.map((s) => (
                        <tr key={s.id} className="border-b align-top">
                          <td className="py-2 px-3">
                            <Chip
                              size="small"
                              color={badgeColor(displayStatus(s.status, s.cancelAt))}
                              label={statusLabel(displayStatus(s.status, s.cancelAt))}
                            />
                          </td>
                          <td className="py-2 px-3">{new Date(s.startDate).toLocaleDateString(locale)}</td>
                          <td className="py-2 px-3">
                            {s.nextInvoiceDate && s.nextInvoiceAmountCents != null ? (
                              <div className="flex flex-col">
                                <span>{new Date(s.nextInvoiceDate).toLocaleDateString(locale)}</span>
                                <span className="text-gray-600 text-sm">
                                  {Intl.NumberFormat(locale, {
                                    style: 'currency',
                                    currency: (s.currency || 'eur').toUpperCase(),
                                  }).format(s.nextInvoiceAmountCents / 100)}
                                </span>
                              </div>
                            ) : (
                              <span>-</span>
                            )}
                          </td>
                          <td className="py-2 px-3">
                            {s.paymentInterval === 'year'
                              ? t('components.ui.manage_subscription.interval.yearly')
                              : t('components.ui.manage_subscription.interval.monthly')}
                          </td>
                          {hasAnyDiscount && (
                            <td className="py-2 px-3">
                              {s.discount ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                  -{s.discount.percentOff}%
                                </span>
                              ) : (
                                <span>-</span>
                              )}
                            </td>
                          )}
                          <td className="py-2 px-3">
                            {s.cancelAt ? new Date(s.cancelAt).toLocaleDateString(locale) : '-'}
                          </td>
                          <td className="py-2 px-3">
                            <ul className="list-disc pl-4">
                              {s.items.map((it) => (
                                <li key={it.priceId}>
                                  {it.productName} <span className="text-gray-500">× {it.quantity}</span>
                                </li>
                              ))}
                            </ul>
                          </td>
                          <td className="py-2 px-3 text-right">
                            {(() => {
                              const isCancelScheduled =
                                s.cancelAt && displayStatus(s.status, s.cancelAt) === 'canceled';
                              const cancelInFuture = s.cancelAt && new Date(s.cancelAt).getTime() > Date.now();

                              if (isCancelScheduled && cancelInFuture) {
                                // Show special promo button if promo code is available and loaded
                                // If still loading promo, show loading state
                                // If promo error or invalid, show regular undo button
                                const buttonText = promoLoading
                                  ? t('components.ui.manage_subscription.button.undo_cancel')
                                  : promoCode
                                    ? t('components.ui.manage_subscription.button.undo_cancel_promo', {
                                        percent: promoCode.percentOff,
                                      })
                                    : t('components.ui.manage_subscription.button.undo_cancel');

                                return (
                                  <button
                                    onClick={() => onUndoCancel(s.id)}
                                    className="text-green-600 hover:text-green-700 underline underline-offset-2 px-0 py-0 bg-transparent whitespace-normal text-right inline-flex items-center gap-1 justify-end"
                                    disabled={promoLoading}
                                  >
                                    {promoLoading && <Spinner size="small" />}
                                    {buttonText}
                                  </button>
                                );
                              } else if (!isCancelScheduled) {
                                return (
                                  <button
                                    onClick={() => onCancel(s.id)}
                                    className="text-red-600 hover:text-red-700 underline underline-offset-2 px-0 py-0 bg-transparent"
                                  >
                                    {t('components.ui.manage_subscription.button.cancel')}
                                  </button>
                                );
                              }
                              return null;
                            })()}
                          </td>
                        </tr>
                      ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Mobile Card View - Hidden on desktop */}
          {!isWixManaged && (
            <div className="lg:hidden space-y-4">
              {loading
                ? Array.from({ length: 3 }).map((_, i) => (
                    <div key={`sk-card-${i}`} className="border rounded-lg p-4 space-y-3">
                      <Skeleton variant="rounded" width={80} height={24} />
                      <Skeleton width="100%" />
                      <Skeleton width="100%" />
                      <Skeleton width="80%" />
                    </div>
                  ))
                : subs.map((s) => {
                    const isCancelScheduled = s.cancelAt && displayStatus(s.status, s.cancelAt) === 'canceled';
                    const cancelInFuture = s.cancelAt && new Date(s.cancelAt).getTime() > Date.now();

                    return (
                      <div key={s.id} className="border rounded-lg p-4 space-y-3">
                        {/* Status */}
                        <div className="flex items-center justify-between">
                          <Chip
                            size="small"
                            color={badgeColor(displayStatus(s.status, s.cancelAt))}
                            label={statusLabel(displayStatus(s.status, s.cancelAt))}
                          />
                          <span className="text-sm text-gray-600">
                            {new Date(s.startDate).toLocaleDateString(locale)}
                          </span>
                        </div>

                        {/* Products */}
                        <div>
                          <div className="text-xs text-gray-500 mb-1">
                            {t('components.ui.manage_subscription.table.products')}
                          </div>
                          <ul className="list-disc pl-4">
                            {s.items.map((it) => (
                              <li key={it.priceId} className="text-sm">
                                {it.productName} <span className="text-gray-500">× {it.quantity}</span>
                              </li>
                            ))}
                          </ul>
                        </div>

                        {/* Payment Details */}
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <div className="text-xs text-gray-500">
                              {t('components.ui.manage_subscription.table.payment_interval')}
                            </div>
                            <div>
                              {s.paymentInterval === 'year'
                                ? t('components.ui.manage_subscription.interval.yearly')
                                : t('components.ui.manage_subscription.interval.monthly')}
                            </div>
                          </div>
                          {s.nextInvoiceDate && s.nextInvoiceAmountCents != null && (
                            <div>
                              <div className="text-xs text-gray-500">
                                {t('components.ui.manage_subscription.table.next_invoice')}
                              </div>
                              <div>{new Date(s.nextInvoiceDate).toLocaleDateString(locale)}</div>
                              <div className="text-gray-600 text-xs">
                                {Intl.NumberFormat(locale, {
                                  style: 'currency',
                                  currency: (s.currency || 'eur').toUpperCase(),
                                }).format(s.nextInvoiceAmountCents / 100)}
                              </div>
                            </div>
                          )}
                          {hasAnyDiscount && s.discount && (
                            <div>
                              <div className="text-xs text-gray-500">
                                {t('components.ui.manage_subscription.table.discount')}
                              </div>
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                -{s.discount.percentOff}%
                              </span>
                            </div>
                          )}
                          {s.cancelAt && (
                            <div>
                              <div className="text-xs text-gray-500">
                                {t('components.ui.manage_subscription.table.contract_end')}
                              </div>
                              <div>{new Date(s.cancelAt).toLocaleDateString(locale)}</div>
                            </div>
                          )}
                        </div>

                        {/* Action Button */}
                        {isCancelScheduled && cancelInFuture ? (
                          <button
                            onClick={() => onUndoCancel(s.id)}
                            className="w-full text-green-600 hover:text-green-700 font-medium py-2 px-4 border border-green-600 rounded hover:bg-green-50 transition-colors flex items-center justify-center gap-2"
                            disabled={promoLoading}
                          >
                            {promoLoading && <Spinner size="small" />}
                            {promoLoading
                              ? t('components.ui.manage_subscription.button.undo_cancel')
                              : promoCode
                                ? t('components.ui.manage_subscription.button.undo_cancel_promo', {
                                    percent: promoCode.percentOff,
                                  })
                                : t('components.ui.manage_subscription.button.undo_cancel')}
                          </button>
                        ) : !isCancelScheduled ? (
                          <button
                            onClick={() => onCancel(s.id)}
                            className="w-full text-red-600 hover:text-red-700 font-medium py-2 px-4 border border-red-600 rounded hover:bg-red-50 transition-colors"
                          >
                            {t('components.ui.manage_subscription.button.cancel')}
                          </button>
                        ) : null}
                      </div>
                    );
                  })}
            </div>
          )}

          <div className="flex justify-center mt-6">
            <button className="bg-primary-root hover:opacity-90 text-white px-6 py-2 rounded" onClick={onClose}>
              {t('components.ui.manage_subscription.button.close')}
            </button>
          </div>
        </div>
      </NotificationModal>
      <AlertDialog
        open={confirmOpen}
        setOpen={setConfirmOpen}
        title={<span>{t('components.ui.manage_subscription.confirm.title')}</span>}
        acceptText={
          cancelLoading
            ? t('components.ui.manage_subscription.confirm.accept_loading')
            : t('components.ui.manage_subscription.confirm.accept')
        }
        message={
          <div className="space-y-3">
            <p>
              {isTrial
                ? t('components.ui.manage_subscription.confirm.question_trial')
                : t('components.ui.manage_subscription.confirm.question_regular')}
            </p>
            <p>
              {isTrial
                ? t('components.ui.manage_subscription.confirm.trial_end_label')
                : t('components.ui.manage_subscription.confirm.contract_end_label')}
              : <strong>{contractualEnd}</strong>
            </p>
            {!isTrial && (
              <p className="text-sm text-gray-600">{t('components.ui.manage_subscription.confirm.legal_notice')}</p>
            )}
            <div className="space-y-2 pt-1">
              <label className="block text-sm font-medium" htmlFor="cancel-reason">
                {t('components.ui.manage_subscription.confirm.reason_label')}
              </label>
              <select
                id="cancel-reason"
                className="dtUiInput w-full"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value as CancellationReason)}
              >
                {CANCELLATION_REASONS.map((reason) => (
                  <option key={reason} value={reason}>
                    {t(`components.ui.manage_subscription.confirm.reasons.${reason}`)}
                  </option>
                ))}
              </select>
              <Textarea
                id="cancel-note"
                rows={2}
                maxLength={CANCELLATION_NOTE_MAX}
                placeholder={t('components.ui.manage_subscription.confirm.note_placeholder')}
                value={cancelNote}
                onChange={(e) => setCancelNote(e.target.value)}
              />
            </div>
          </div>
        }
        onAccept={() => {
          if (!cancelSubId) return;

          runCancel({
            variables: {
              subscriptionId: cancelSubId,
              reason: cancelReason,
              note: cancelNote.trim() || undefined,
            },
          })
            .then(() => {
              refetch();
            })
            .catch((e) => {
              console.error(e);
            })
            .finally(() => {
              setCancelSubId(null);
            });
        }}
      />
      <AlertDialog
        open={undoConfirmOpen}
        setOpen={setUndoConfirmOpen}
        title={
          <span>
            {promoCode
              ? t('components.ui.manage_subscription.undo_confirm.title_promo')
              : t('components.ui.manage_subscription.undo_confirm.title')}
          </span>
        }
        acceptText={
          undoLoading
            ? t('components.ui.manage_subscription.undo_confirm.accept_loading')
            : promoCode
              ? t('components.ui.manage_subscription.undo_confirm.accept_promo', { percent: promoCode.percentOff })
              : t('components.ui.manage_subscription.undo_confirm.accept')
        }
        message={
          <div className="space-y-3">
            <div>
              {promoCode
                ? t('components.ui.manage_subscription.undo_confirm.question_promo', { percent: promoCode.percentOff })
                : t('components.ui.manage_subscription.undo_confirm.question')}
            </div>
            <div>
              {t('components.ui.manage_subscription.undo_confirm.cancel_date_label')}: <strong>{undoCancelAt}</strong>
            </div>
            {promoCode && (
              <div className="bg-green-50 border border-green-200 rounded p-3">
                <div className="font-semibold text-green-800">
                  {t('components.ui.manage_subscription.undo_confirm.promo_benefit', {
                    percent: promoCode.percentOff,
                  })}
                </div>
                {/* <div className="text-sm text-green-700 mt-1">
                  {t('components.ui.manage_subscription.undo_confirm.promo_code')}: <strong>{promoCode.code}</strong>
                </div> */}
              </div>
            )}
            <div className="text-sm text-gray-600">
              {promoCode
                ? t('components.ui.manage_subscription.undo_confirm.description_promo')
                : t('components.ui.manage_subscription.undo_confirm.description')}
            </div>
          </div>
        }
        onAccept={() => {
          if (!undoSubId) return;

          runUndoCancel({
            variables: {
              subscriptionId: undoSubId,
              promoCodeId: promoCode ? promoCodeId : undefined,
            },
          })
            .then(() => {
              refetch();
            })
            .catch((e) => {
              console.error(e);
            })
            .finally(() => {
              setUndoSubId(null);
            });
        }}
      />
    </>
  );
}
